<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's',
        [$style.mediumScreen]: $screen === 'm',
        [$style.largeScreen]: $screen === 'l'
      }
    ]"
  >
    <router-view />
    <BaseSpinner v-if="loading" />
    <Flex
      v-else
      direction="column"
      :gap="2"
    >
      <BaseAlert
        v-if="!contactEmailVerified"
        :text="t('email_templates.verification_alert.text')"
        color="warning"
        :primaryAction="t('email_templates.verification_alert.action')"
        @primaryAction="verifyContactEmail"
        v-test="'contact-email-verification-alert'"
      />
      <div :class="$style.templates">
        <Flex
          v-for="(group, index) in groupedTemplates"
          :key="index"
          direction="column"
        >
          <div :class="$style.templateHeading">
            <BaseHeading>
              {{ $t(`email_templates.categories.${group.name.toLowerCase()}`) }}
            </BaseHeading>
          </div>
          <div>
            <div
              v-for="(item, itemIndex) in group.items"
              :key="itemIndex"
              @click="!contactEmailVerified ? showVerificationModal() : null"
            >
              <component
                :is="!contactEmailVerified ? 'span' : 'router-link'"
                :to="{
                  name: 'message-template',
                  params: {
                    id: item.id
                  }
                }"
                v-test="'company-email-template'"
              >
                <TemplateLink :heading="item.heading" />
              </component>
            </div>
          </div>
        </Flex>
      </div>
      <Banner
        v-if="!loading && !isNativeAppIOS"
        v-test="'email-templates-banner'"
      />
    </Flex>
  </div>
</template>

<script lang="ts">
export default {
  name: 'EmailTemplates',
};
</script>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { useI18n } from 'vue-i18n';
import { groupBy } from '@/helpers/formatting';
import { modal } from '@/helpers/ui';
import { useContactEmailVerification } from '@/modules/marketing/useContactEmailVerification';
import { useNativeAppStore } from '@/stores/native-app';
import TemplateLink from '../TemplateLink.vue';
import Banner from './Banner.vue';
import { useTemplateData } from './data';

const { templates } = useTemplateData();
const { t } = useI18n();
const { contactEmailVerified, verifyContactEmail } =
  useContactEmailVerification();

const { isNativeAppIOS } = useNativeAppStore();

const showVerificationModal = () => {
  modal('confirmation', {
    type: 'sendEmail',
    subMessage: t('email_templates.verification_modal.sub_message'),
    message: t('email_templates.verification_modal.message'),
  }).then(() => {
    verifyContactEmail();
  });
};

const { result, loading } = useQuery(gql`
  query getCompanyEmailTemplates {
    companyEmailTemplates {
      id
    }
  }
`);

const fetchedTemplates = computed(
  () => result.value?.companyEmailTemplates || [],
);

const groupedTemplates = computed(() => {
  const filteredTemplates = templates.filter(
    (template) =>
      !!fetchedTemplates.value.find((t: any) => t.id === template.id),
  );
  return groupBy(filteredTemplates, 'category').map((group, index) => ({
    ...group,
    slot: index + 1,
  }));
});
</script>

<style
  lang="scss"
  module
>
.base {
  &:not(.smallScreen) {
    padding: $spacing;
  }
}

.templates {
  display: flex;
  gap: $spacing * 2;

  .base:not(.smallScreen) & {
    flex-wrap: wrap;
  }

  .base.smallScreen & {
    flex-direction: column;
    padding-top: $spacing;
  }

  & > * {
    .base.mediumScreen & {
      width: calc(50% - #{$spacing * 3});
    }

    .base.largeScreen & {
      width: calc(25% - #{$spacing * 1.5});
    }
  }
}

.templateHeading {
  padding: 0 $spacing;
}

.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
